import { useHasAllocations } from '@/allocation/composables/useHasAllocations';
import auth from '@/auth/auth';
import {
    CreateMultiLinkDocument,
    DeleteExternalEventEventDocument,
    DeleteExternalEventLocationDocument,
    ExternalEventVariantsWithAllocationsCountDocument,
    GetExternalEventEventDataDocument,
    UpdateExternalEventEventDocument,
    UpdateExternalEventLocationDocument,
    UpdateExternalEventMultiLinksDocument,
} from '@/generated/graphql';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { useFluent } from 'fluent-vue';
import { computed, Ref, toValue } from 'vue';
import { useRouter } from 'vue-router';
import { ExternalEventEventUpdateSchemaType } from '../schemas';

const DEFAULT_EXTERNAL_EVENT_EVENT_STATUS = 'fix';

export function useExternalEventEvent(eventId: Ref<string>) {
    const isAdmin = auth.isAdmin();
    const fluent = useFluent();
    const router = useRouter();

    const {
        result: getDataQuery,
        loading,
        refetch,
    } = useQuery(
        GetExternalEventEventDataDocument,
        computed(() => ({ id: toValue(eventId) })),
    );
    const event = computed(() => getDataQuery.value?.externalEvent ?? undefined);
    const status = computed(() => event.value?.status ?? DEFAULT_EXTERNAL_EVENT_EVENT_STATUS);

    const deleteMutation = useMutation(DeleteExternalEventEventDocument);
    const deleteDisabledExplanation = computed(() => {
        if (!isAdmin) return fluent.$t('no-permission-to-edit');
        if (event.value?.variantsWithAllocations && event.value?.variantsWithAllocations.totalCount > 0) {
            return fluent.$t('external-event-event-has-allocations');
        }
        return undefined;
    });
    const deleteLocationMutation = useMutation(DeleteExternalEventLocationDocument);

    async function deleteEvent() {
        const eventData = event.value;
        if (eventData) {
            const hasAllocations = eventData.variantsWithAllocations.totalCount > 0;
            if (!isAdmin || hasAllocations) return;
            if (!confirm(fluent.$t('confirm-delete-external-event', { name: event.value?.nameShort ?? '' }))) return;

            if (event.value?.locationId) {
                await deleteLocationMutation.mutate({ id: event.value.locationId });
            }
            const res = await deleteMutation.mutate({ id: toValue(eventId) });
            if (res?.data) {
                router.replace({
                    name: 'external-event-series',
                    params: { seriesId: toValue(event.value?.series.id) },
                });
            }
        }
    }

    const updateMutation = useMutation(UpdateExternalEventEventDocument);
    async function onUpdateEvent(data: ExternalEventEventUpdateSchemaType) {
        const result = await updateMutation.mutate({
            id: toValue(eventId),
            ...data,
        });
        if (result?.errors) {
            return false;
        }
        refetch();
        return true;
    }

    const updateLocationMutation = useMutation(UpdateExternalEventLocationDocument);
    async function updateLocation(locationId: string, name: string) {
        const result = await updateLocationMutation.mutate({
            locationId,
            name,
        });
        if (result?.errors) {
            return false;
        }
        return true;
    }

    const updateEventMultiLinksMutation = useMutation(UpdateExternalEventMultiLinksDocument);
    async function updateExternalEventMultiLinkId(fieldName: string, multiLinkId: string) {
        if (!event.value) return;
        const response = await updateEventMultiLinksMutation.mutate({
            id: event.value.id,
            [fieldName]: multiLinkId,
        });
        if (response?.errors) {
            throw new Error(`Could not update external event multilink ${fieldName}: ${response.errors}`);
        }
    }

    const createMultiLinkMutation = useMutation(CreateMultiLinkDocument);
    async function createExternalEventMultiLink(fieldName: string) {
        if (!event.value) return;
        const name = `${event.value.id}-${fieldName}`;
        const response = await createMultiLinkMutation.mutate({ name });
        if (response?.errors) {
            throw new Error(`Could not create Multilink for external event: ${response.errors}`);
        }
        if (response?.data) {
            const multiLinkId = response.data.createMultilink?.multilink?.id;
            if (multiLinkId) {
                await updateExternalEventMultiLinkId(`${fieldName}Id`, multiLinkId);
                return multiLinkId;
            }
        }
    }

    const { refetchHasAllocations } = useHasAllocations(ExternalEventVariantsWithAllocationsCountDocument, eventId);

    return {
        event,
        loading,
        status,
        deleteDisabledExplanation,
        deleteEvent,
        onUpdateEvent,
        updateLocation,
        refetchHasAllocations,
        createExternalEventMultiLink,
    };
}
